/**
 * «Источники → По дням»: уникальные рабочие номера по каналам строкой на день,
 * неделю или месяц; сайт — одна колонка, каналы без рабочих номеров вне отбора.
 * Второй период (`cfrom`/`cto`) — сравнение: строки сопоставлены по порядку
 * (1-й день с 1-м днём), в ячейке — значение, под ним значение сравнения и
 * изменение. Спека `docs/specs/2026-09-30-leads-sources-design.md`, «По дням»;
 * числа — `prokat-runner leads-sources --days`.
 */
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { iso, plusDays } from "@/lib/dates";
import { type ChannelCells, type ChannelPeriod, type PhoneCounts, loadLeadSourcesDays } from "@/lib/leadSources";

type Group = "day" | "week" | "month";
const GROUP: Record<Group, { label: string; head: string; rows: "days" | "weeks" | "months" }> = {
  day: { label: "по дням", head: "День", rows: "days" },
  week: { label: "по неделям", head: "Неделя", rows: "weeks" },
  month: { label: "по месяцам", head: "Месяц", rows: "months" },
};

type Metric = { id: string; label: string; hint: string; value: (c: PhoneCounts) => number | null; rate?: boolean };
const rate = (a: number, b: number) => (b ? (a * 100) / b : null);
const METRICS: Metric[] = [
  { id: "phones", label: "Номера", hint: "уникальные рабочие номера (без дублей, спама, рекламы…)", value: (c) => c.phones },
  { id: "new", label: "Новые", hint: "номер без лидов за 90 дней до лида", value: (c) => c.new },
  { id: "deal", label: "Сделка", hint: "у лида номера есть сделка", value: (c) => c.deal },
  { id: "recorded", label: "Записали", hint: "сделка входила в «ждем клиента» после лида", value: (c) => c.recorded },
  { id: "booked", label: "Бронь", hint: "договор → «Бронь»/«Подписан» после лида", value: (c) => c.booked },
  { id: "recorded_rate", label: "% записи", hint: "записали / номера", value: (c) => rate(c.recorded, c.phones), rate: true },
  { id: "booked_rate", label: "% брони", hint: "бронь / номера", value: (c) => rate(c.booked, c.phones), rate: true },
];

const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

function dm(day: string, year = false): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}${year ? `.${day.slice(2, 4)}` : ""}`;
}

function title(d: { day: string; to: string }, group: Group, year: boolean): string {
  if (group === "month") {
    const x = new Date(`${d.day}T12:00:00`);
    return `${x.toLocaleDateString("ru-RU", { month: "short" })} ${d.day.slice(0, 4)}`;
  }
  if (d.day === d.to) return `${dm(d.day, year)} ${WEEKDAY[new Date(`${d.day}T12:00:00`).getDay()]}`;
  return `${dm(d.day, year)} – ${dm(d.to, year)}`;
}

function minusYear(day: string): string {
  const x = new Date(`${day}T12:00:00`);
  x.setFullYear(x.getFullYear() - 1);
  return iso(x);
}

function fmt(v: number | null, m: Metric): string {
  if (v === null) return "—";
  return m.rate ? `${v.toFixed(1)}%` : String(v);
}

/** Изменение: для счётчиков — в %, для долей — в п. п. */
function Delta({ a, b, m }: { a: number | null; b: number | null; m: Metric }) {
  if (a === null || b === null) return null;
  if (!m.rate && b === 0) return a ? null : <span className="text-fg-faint">±0</span>;
  const d = m.rate ? a - b : ((a - b) * 100) / b;
  if (Math.abs(d) < 0.5) return <span className="text-fg-faint">±0</span>;
  const text = `${d > 0 ? "+" : "−"}${Math.abs(d).toFixed(m.rate ? 1 : 0)}${m.rate ? " п.п." : "%"}`;
  return <span className={d > 0 ? "text-ok" : "text-err"}>{text}</span>;
}

function Cell({ a, b, m, strong }: { a: PhoneCounts | undefined; b: PhoneCounts | undefined | null; m: Metric; strong?: boolean }) {
  const va = a ? m.value(a) : null;
  const vb = b ? m.value(b) : null;
  return (
    <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${strong ? "border-l border-border" : ""}`}>
      <div className={`text-[14px] font-semibold ${va ? "text-fg" : "text-fg-faint"}`}>{fmt(va, m)}</div>
      {b !== null && (
        <div className="text-[11.5px] text-fg-muted">
          {fmt(vb, m)} <Delta a={va} b={vb} m={m} />
        </div>
      )}
    </td>
  );
}

function Row({
  label,
  sub,
  a,
  b,
  keys,
  m,
  className,
}: {
  label: string;
  sub?: string | undefined;
  a: ChannelCells | undefined;
  b: ChannelCells | undefined | null;
  keys: string[];
  m: Metric;
  className: string;
}) {
  return (
    <tr className={className} data-annot={`строка ${label}`}>
      <td className="whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums">
        <div>{label}</div>
        {sub && <div className="text-[11.5px] text-fg-muted">{sub}</div>}
      </td>
      {keys.map((k) => (
        <Cell key={k} a={a?.by[k]} b={b === null ? null : b?.by[k]} m={m} />
      ))}
      <Cell a={a?.all} b={b === null ? null : b?.all} m={m} strong />
    </tr>
  );
}

export function LeadSourcesDaysSection() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const cfrom = params.get("cfrom") || "";
  const cto = params.get("cto") || "";
  const compare = Boolean(cfrom && cto);
  const g = params.get("group");
  const group: Group = g === "week" || g === "month" ? g : "day";
  const m = METRICS.find((x) => x.id === params.get("metric")) ?? METRICS[0]!;
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({
    queryKey: ["leads-sources-days", from, to, cfrom, cto],
    queryFn: () => loadLeadSourcesDays(from, to, cfrom, cto),
  });
  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const r = q.data;
  const a: ChannelPeriod | undefined = r?.periods[0];
  const b: ChannelPeriod | null = (compare && r?.periods[1]) || null;
  const keys = r?.channels.map((c) => c.key) ?? [];
  const rowsA = a ? a[GROUP[group].rows] : [];
  const rowsB = b ? b[GROUP[group].rows] : [];
  const crossYear = compare && from.slice(0, 4) !== cfrom.slice(0, 4);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="период по дням источники">
        <DateRangePicker label="Период" from={from} to={to} onChange={(x, y) => set({ from: x, to: y })} />
        {compare ? (
          <div className="flex items-center gap-1">
            <DateRangePicker label="Сравнить с" from={cfrom} to={cto} onChange={(x, y) => set({ cfrom: x, cto: y })} />
            <button
              type="button"
              onClick={() => set({ cfrom: "", cto: "" })}
              className="grid h-7 w-7 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:text-fg"
              aria-label="Убрать сравнение"
              title="Убрать сравнение"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex gap-1">
            <button type="button" className={seg(false)} onClick={() => set({ cfrom: minusYear(from), cto: minusYear(to) })}>
              + сравнить с прошлым годом
            </button>
            <button
              type="button"
              className={seg(false)}
              onClick={() => {
                const n = Math.round((Date.parse(`${to}T12:00:00`) - Date.parse(`${from}T12:00:00`)) / 86_400_000) + 1;
                const start = new Date(`${from}T12:00:00`);
                set({ cfrom: iso(plusDays(start, -n)), cto: iso(plusDays(start, -1)) });
              }}
            >
              + с предыдущим периодом
            </button>
          </div>
        )}
        <div className="flex gap-1" role="group" aria-label="Группировка">
          {(Object.keys(GROUP) as Group[]).map((k) => (
            <button key={k} type="button" onClick={() => set({ group: k === "day" ? "" : k })} aria-pressed={group === k} className={seg(group === k)}>
              {GROUP[k].label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Показатель">
          {METRICS.map((x) => (
            <button
              key={x.id}
              type="button"
              title={x.hint}
              onClick={() => set({ metric: x.id === "phones" ? "" : x.id })}
              aria-pressed={m.id === x.id}
              className={seg(m.id === x.id)}
            >
              {x.label}
            </button>
          ))}
        </div>
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю номера по каналам…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить источники: {String(q.error.message)}</div>}
      {r && a && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`источники по дням ${m.label}`}>
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`}>{GROUP[group].head}</th>
                  {r.channels.map((c) => (
                    <th key={c.key} className={`${th} text-right`}>
                      {c.label}
                    </th>
                  ))}
                  <th className={`${th} border-l border-border text-right`}>Итого</th>
                </tr>
              </thead>
              <tbody>
                <Row
                  label="За период"
                  sub={b ? `${title({ day: a.from, to: a.to }, "day", true)} против ${title({ day: b.from, to: b.to }, "day", true)}` : undefined}
                  a={a.total}
                  b={b ? b.total : null}
                  keys={keys}
                  m={m}
                  className="border-t border-border bg-bg font-medium"
                />
                {rowsA.map((x, i) => {
                  const y = b ? (rowsB[i] ?? undefined) : null;
                  const wd = new Date(`${x.day}T12:00:00`).getDay();
                  return (
                    <Row
                      key={x.day}
                      label={title(x, group, false)}
                      sub={y ? title(y, group, crossYear) : undefined}
                      a={x}
                      b={y}
                      keys={keys}
                      m={m}
                      className={`border-t border-border hover:bg-bg-hover ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            Только уникальные рабочие номера (без дублей, спама, рекламы, вакансий); номер в строке — один раз, «За
            период» и «Итого» — уникальные заново, не сумма. Сайт — любая заявка сайта (попап, баннер, заказ) или
            источник WEB. Вне отбора каналы почти без рабочих номеров — «Задать вопрос», e-mail, вручную, чаты: за
            период {a.total.excluded.phones} номеров
            {b ? ` (в сравнении ${b.total.excluded.phones})` : ""}.
            {b && " Сравнение — строки по порядку: 1-й день с 1-м днём, 1-я неделя с 1-й неделей; под значением — период сравнения и изменение."}
          </div>
        </section>
      )}
    </>
  );
}
